import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";

const CHEVRONS = {
  left: ChevronLeft,
  right: ChevronRight,
  down: ChevronDown,
};

const Chevron = ({ orientation, className, ...props }) => {
  const Icon = CHEVRONS[orientation] || ChevronRight;
  return <Icon className={cn("h-4 w-4", className)} {...props} />;
};

/**
 * Theme-styled react-day-picker. Pass any DayPicker props (mode, selected,
 * onSelect, captionLayout, disabled, ...); visual defaults follow the app
 * palette in both themes.
 */
const Calendar = ({ className, classNames, showOutsideDays = true, ...props }) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    className={cn("p-3", className)}
    classNames={{
      months: "relative flex flex-col sm:flex-row gap-2",
      month: "flex w-full flex-col gap-3",
      nav: "absolute inset-x-0 top-0 z-10 flex items-center justify-between",
      button_previous: cn(
        buttonVariants({ variant: "ghost" }),
        "h-7 w-7 p-0 opacity-70 hover:opacity-100",
      ),
      button_next: cn(
        buttonVariants({ variant: "ghost" }),
        "h-7 w-7 p-0 opacity-70 hover:opacity-100",
      ),
      month_caption: "mx-8 flex h-7 items-center justify-center",
      dropdowns: "flex items-center justify-center gap-1.5",
      // The native <select> sits invisibly over the styled caption span
      // (rdp renders both; styling both shows the label twice).
      dropdown_root:
        "relative flex items-center rounded-md border border-input bg-background px-2 py-0.5 hover:bg-muted focus-within:ring-2 focus-within:ring-ring",
      dropdown: "absolute inset-0 cursor-pointer opacity-0",
      caption_label:
        "flex select-none items-center gap-1 text-sm font-medium",
      month_grid: "w-full border-collapse",
      weekdays: "flex",
      weekday:
        "w-9 text-center text-[0.8rem] font-normal text-muted-foreground",
      week: "mt-1.5 flex",
      day: "relative h-9 w-9 p-0 text-center text-sm",
      day_button: cn(
        buttonVariants({ variant: "ghost" }),
        "h-9 w-9 p-0 font-normal",
      ),
      selected:
        "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary/90",
      today: "[&>button]:border [&>button]:border-primary/50 font-medium",
      outside: "[&>button]:text-muted-foreground [&>button]:opacity-50",
      disabled: "[&>button]:text-muted-foreground [&>button]:opacity-40",
      hidden: "invisible",
      ...classNames,
    }}
    components={{ Chevron }}
    {...props}
  />
);
Calendar.displayName = "Calendar";

export { Calendar };
